import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { ContestantTable } from '../../../components/admin/contestants/ContestantTable';
import { CreateContestantButton } from '../../../components/admin/contestants/CreateContestantButton';
import { ContestantFilters } from '../../../components/admin/contestants/ContestantFilters';
import { Users, Loader2, Link as LinkIcon, Copy, Check } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { Button } from '../../../components/ui/button';
import { useCheckPermissions } from '../../../middleware/checkPermissions';

interface ContestantLinks {
  contest_name: string;
  contestants: {
    name: string;
    id: string;
    url: string;
  }[];
}

export const ContestantList = () => {
  useCheckPermissions(['admin']);
  const [stats, setStats] = useState({
    total: 0,
    totalVotes: 0,
    averageVotes: 0,
    contests: 0
  });
  const [activeTab, setActiveTab] = useState<'list' | 'links'>('list');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedContest, setSelectedContest] = useState<string | null>(null);
  const [contestsData, setContestsData] = useState<Array<{
    name: string;
    slug: string;
    code_unique?: string;
    contestants: Array<{
      name: string;
      unique_code: string;
    }>;
  }>>([]);
  const [copied, setCopied] = useState(false);

  const fetchContests = async () => {
    try {
      const { data: contests } = await supabase
        .from('contests')
        .select(`
          name,
          slug,
          code_unique,
          status,
          end_date,
          contestants (
            name,
            unique_code
          )
        `)
        .order('name');

      if (!contests) return;

      // Exclure les concours terminés (status = 'completed' ou end_date < aujourd'hui)
      const now = new Date();
      const filtered = contests.filter((contest: any) => {
        if (contest.status === 'completed') return false;
        if (contest.end_date && new Date(contest.end_date) < now) return false;
        return true;
      });

      setContestsData(
        filtered
          .map((contest: any) => ({
            name: contest.name,
            slug: contest.slug,
            code_unique: contest.code_unique,
            contestants: contest.contestants || []
          }))
      );
    } catch (error) {
      console.error('Erreur lors de la récupération des concours:', error);
    }
  };

  const getContestLinks = (contestName: string) => {
    const contest = contestsData.find(c => c.name === contestName);
    if (!contest) return '';
    
    // Ajouter le lien d'inscription au concours
    const registrationLink = `https://votafrica.com/inscription/${contest.code_unique}`;
    
    return `# Lien du concours ou de la Catégorie\nhttps://votafrica.com/c/${contest.slug}\n\n# Lien d'inscription\n${registrationLink}\n\n# Liens des Candidats\n${contest.contestants
      .map((c, i) => `${i + 1}. ${c.name}\nhttps://votafrica.com/${c.unique_code}`)
      .join('\n\n')}`;
  };

  const copyToClipboard = async (text: string) => {
    try {
      // Méthode 1 : API Clipboard moderne
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        return;
      }

      // Méthode 2 : Solution de repli avec élément temporaire
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        textArea.remove();
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Erreur lors de la copie (fallback):', err);
        textArea.remove();
      }
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { count: totalContestants } = await supabase
          .from('contestants')
          .select('*', { count: 'exact', head: true });

        const { count: totalVotes } = await supabase
          .from('votes')
          .select('*', { count: 'exact', head: true });

        const { count: totalContests } = await supabase
          .from('contests')
          .select('*', { count: 'exact', head: true });

        setStats({
          total: totalContestants || 0,
          totalVotes: totalVotes || 0,
          averageVotes: totalContestants ? Math.round((totalVotes || 0) / totalContestants) : 0,
          contests: totalContests || 0
        });
        setIsLoading(false);
      } catch (error) {
        console.error('Erreur lors de la récupération des statistiques:', error);
        setIsLoading(false);
      }
    };

    fetchStats();
    if (activeTab === 'links') {
      fetchContests();
    }
  }, [activeTab]);

  return (
    <AdminLayout>
      <div className="py-8 space-y-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="absolute -inset-2 bg-gradient-to-r from-yellow-600 via-yellow-400 to-yellow-600 rounded-full blur opacity-75"></div>
                  <Users className="h-8 w-8 text-yellow-400 relative" />
                </div>
                <h1 className="text-2xl font-bold">
                  <span className="bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-200 text-transparent bg-clip-text">
                    Gestion des Candidats
                  </span>
                </h1>
              </div>
              {activeTab === 'list' && <CreateContestantButton />}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-400">Total Candidats</p>
                  <span className="text-2xl font-bold text-white">{stats.total}</span>
                </div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-400">Total Votes</p>
                  <span className="text-2xl font-bold text-yellow-400">{stats.totalVotes}</span>
                </div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-400">Moyenne Votes</p>
                  <span className="text-2xl font-bold text-green-400">{stats.averageVotes}</span>
                </div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-400">Concours</p>
                  <span className="text-2xl font-bold text-blue-400">{stats.contests}</span>
                </div>
              </div>
            </div>

            <div className="flex space-x-4 border-b border-white/10">
              <button
                className={`px-4 py-2 font-medium ${
                  activeTab === 'list'
                    ? 'text-yellow-400 border-b-2 border-yellow-400'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
                onClick={() => setActiveTab('list')}
              >
                Liste des candidats
              </button>
              <button
                className={`px-4 py-2 font-medium ${
                  activeTab === 'links'
                    ? 'text-yellow-400 border-b-2 border-yellow-400'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
                onClick={() => setActiveTab('links')}
              >
                Liens à copier
              </button>
            </div>

            {activeTab === 'list' ? (
              <>
                <ContestantFilters />
                <ContestantTable />
              </>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {contestsData.map(contest => (
                    <button
                      key={contest.name}
                      onClick={() => setSelectedContest(contest.name)}
                      className={`p-4 rounded-lg text-left transition-colors ${
                        selectedContest === contest.name
                          ? 'bg-yellow-400/20 border border-yellow-400/50'
                          : 'bg-white/5 border border-white/10 hover:bg-white/10'
                      }`}
                    >
                      <h3 className="font-medium text-white">{contest.name}</h3>
                      <p className="text-sm text-gray-400">
                        {contest.contestants.length} candidat{contest.contestants.length > 1 ? 's' : ''}
                      </p>
                    </button>
                  ))}
                </div>

                {selectedContest && (
                  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-medium text-white">{selectedContest}</h3>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(getContestLinks(selectedContest))}
                      >
                        {copied ? (
                          <Check className="h-4 w-4 mr-2" />
                        ) : (
                          <Copy className="h-4 w-4 mr-2" />
                        )}
                        {copied ? 'Copié !' : 'Copier'}
                      </Button>
                    </div>
                    <pre className="bg-black/20 p-4 rounded-lg text-gray-300 whitespace-pre-wrap font-mono text-sm">
                      {getContestLinks(selectedContest)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ContestantList;
